import { normalizeCompanyName } from './normalise.js';
import { contactRelevance } from './scoring.js';
import { isOpportunitySnoozed, opportunityTimingStage, validOpportunityTime } from './opportunity-timing.js';
const DAY = 86_400_000;
function opportunityCompanyKeys(opportunity) {
    const keys = new Set();
    if (opportunity.companyName)
        keys.add(normalizeCompanyName(opportunity.companyName));
    for (const target of opportunity.targetCompanies || [])
        keys.add(normalizeCompanyName(target.name));
    keys.delete('');
    return keys;
}
function proofMatch(opportunity, caseStudies) {
    const sport = String(opportunity.sport || '').toLowerCase();
    const tags = (opportunity.serviceTags || []).map(value => value.toLowerCase());
    return caseStudies.some(item => item.active !== false && ((sport && item.sports.some(value => value.toLowerCase() === sport)) ||
        item.tags.some(tag => tags.some(signal => signal.includes(tag.toLowerCase()) || tag.toLowerCase().includes(signal)))));
}
export function opportunityFitAssessment(opportunity, contacts = [], sequences = [], companies = [], caseStudies = []) {
    if (isOpportunitySnoozed(opportunity))
        return { score: 0, strong: false, reasons: ['Snoozed'], reachableHighFitContacts: 0 };
    const stage = opportunityTimingStage(opportunity);
    let score = 0;
    const reasons = [];
    if (stage === 'contact-now') {
        score += 30;
        reasons.push('Contact now');
    }
    else if (stage === 'opening-soon') {
        score += 21;
        reasons.push('Planning window');
    }
    else if (stage === 'too-early') {
        score += 8;
        reasons.push('Planning ahead');
    }
    else if (stage === 'too-late') {
        score -= 35;
        reasons.push('Past primary window');
    }
    if (opportunity.priority === 'high') {
        score += 12;
        reasons.push('High-priority opportunity');
    }
    else if (opportunity.priority === 'medium')
        score += 6;
    if (opportunity.type === 'new-partnership') {
        score += 10;
        reasons.push('Partnership trigger');
    }
    else if (opportunity.type === 'future-event') {
        score += 9;
        reasons.push('Event opportunity');
    }
    else if (opportunity.type === 'live-brief') {
        score += 14;
        reasons.push('Live brief');
    }
    else if (opportunity.type === 'account-trigger')
        score += 7;
    const serviceText = (opportunity.serviceTags || []).join(' ').toLowerCase();
    const serviceSignals = ['partnership', 'sponsor', 'experiential', 'experience', 'fan', 'activation', 'campaign', 'motion', 'creative', 'content', 'hospitality', 'brand'];
    const matchedServices = serviceSignals.filter(signal => serviceText.includes(signal));
    score += Math.min(14, matchedServices.length * 3);
    if (matchedServices.length >= 2)
        reasons.push('Strong BAILEY service fit');
    const keys = opportunityCompanyKeys(opportunity);
    const companyIds = new Set(companies.filter(company => keys.has(normalizeCompanyName(company.name))).map(company => company.id));
    const linkedContacts = contacts.filter(contact => !contact.archived && !contact.doNotContact && (companyIds.has(contact.companyId) || keys.has(normalizeCompanyName(contact.companyName))));
    const sentIds = new Set(sequences.filter(sequence => !sequence.archived && sequence.stage === 'sent').map(sequence => sequence.contactId));
    const activeIds = new Set(sequences.filter(sequence => !sequence.archived && sequence.stage !== 'sent').map(sequence => sequence.contactId));
    const untouched = linkedContacts.filter(contact => !sentIds.has(contact.id) && !activeIds.has(contact.id) && contact.deadLeadReason !== 'no-longer-at-company');
    const strongContacts = untouched.filter(contact => {
        const fit = contactRelevance(contact.title) + (contact.emailVerification === 'verified' ? 7 : contact.email ? 3 : 0);
        return fit >= 78;
    });
    if (strongContacts.length) {
        score += Math.min(15, 9 + Math.min(6, strongContacts.length * 2));
        reasons.push(`${strongContacts.length} reachable high-fit contact${strongContacts.length === 1 ? '' : 's'}`);
    }
    else if (untouched.length) {
        score += 5;
        reasons.push('Untouched CRM contacts available');
    }
    const opportunitySequences = sequences.filter(sequence => !sequence.archived && sequence.opportunityId === opportunity.id);
    if (opportunitySequences.some(sequence => sequence.stage === 'sent'))
        score -= 6;
    if (opportunitySequences.filter(sequence => sequence.stage === 'scheduled' || sequence.stage === 'queue').length >= 2)
        score -= 8;
    if (caseStudies.length && proofMatch(opportunity, caseStudies)) {
        score += 6;
        reasons.push('Relevant proof available');
    }
    if (opportunity.sourceUrl)
        score += 2;
    score = Math.max(0, Math.min(100, Math.round(score)));
    return { score, strong: score >= 72 && stage !== 'too-late', reasons, reachableHighFitContacts: strongContacts.length };
}
export function topOpportunityIds(opportunities, contacts = [], sequences = [], companies = [], caseStudies = [], limit = 3) {
    return new Set(opportunities
        .map(opportunity => ({ opportunity, assessment: opportunityFitAssessment(opportunity, contacts, sequences, companies, caseStudies) }))
        .filter(item => item.assessment.strong)
        .sort((a, b) => b.assessment.score - a.assessment.score || (validOpportunityTime(a.opportunity.outreachStart) ?? Number.MAX_SAFE_INTEGER) - (validOpportunityTime(b.opportunity.outreachStart) ?? Number.MAX_SAFE_INTEGER))
        .slice(0, Math.max(1, limit))
        .map(item => item.opportunity.id));
}
export function isOpportunityNew(opportunity, now = Date.now(), hours = 72) {
    const first = validOpportunityTime(opportunity.firstSeenAt);
    const reviewed = validOpportunityTime(opportunity.reviewedAt);
    if (reviewed != null && first != null && reviewed >= first)
        return false;
    return opportunity.source === 'radar' && first != null && now >= first && now - first <= hours * 3_600_000;
}
export function opportunityTimingTransition(opportunity, now = Date.now()) {
    const stage = opportunityTimingStage(opportunity, now);
    const start = validOpportunityTime(opportunity.outreachStart);
    if (stage === 'opening-soon' && start != null) {
        const days = Math.max(1, Math.ceil((start - now) / DAY));
        return { label: `CONTACT IN ${days}D`, detail: `Contact from ${new Date(start).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}` };
    }
    if (stage === 'too-early' && start != null) {
        const days = Math.max(1, Math.ceil((start - now) / DAY));
        return { label: `CONTACT IN ${days}D`, detail: `Contact from ${new Date(start).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}` };
    }
    if (stage === 'contact-now')
        return { label: 'CONTACT NOW', detail: start != null ? `Window opened ${new Date(start).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}` : 'Recommended outreach window is open' };
    return undefined;
}
export function opportunityConversion(opportunity, contacts = [], sequences = []) {
    const linkedContacts = new Set(contacts.filter(contact => contact.sourceOpportunityId === opportunity.id || (contact.sourceOpportunityIds || []).includes(opportunity.id)).map(contact => contact.id));
    const linkedSequences = sequences.filter(sequence => !sequence.archived && (sequence.opportunityId === opportunity.id || linkedContacts.has(sequence.contactId)));
    for (const sequence of linkedSequences)
        linkedContacts.add(sequence.contactId);
    const sent = linkedSequences.filter(sequence => sequence.stage === 'sent');
    const humanReplyOutcomes = new Set(['reply-positive', 'reply-neutral', 'not-interested', 'not-now', 'meeting-booked', 'brief-received', 'project-won']);
    return {
        peopleFound: linkedContacts.size,
        prepared: linkedSequences.filter(sequence => sequence.stage === 'queue' || sequence.stage === 'scheduled' || sequence.stage === 'sent').length,
        sent: sent.length,
        replies: sent.filter(sequence => humanReplyOutcomes.has(sequence.outcome)).length,
        meetings: sent.filter(sequence => ['meeting-booked', 'brief-received', 'project-won'].includes(sequence.outcome)).length,
        briefs: sent.filter(sequence => ['brief-received', 'project-won'].includes(sequence.outcome)).length,
        projects: sent.filter(sequence => sequence.outcome === 'project-won').length,
    };
}
